import { Container, Graphics, Text } from 'pixi.js';
import { gsap } from 'gsap';
import type { BracketState } from '@fbwl/shared';

const COLUMN_WIDTH = 118;
const SLOT_HEIGHT = 26;
const SLOT_GAP = 6;

/**
 * The knockout ladder.
 *
 * A compact, always-visible map of where the tournament is: who has already won
 * through, who is playing now, and how many rounds are left. Viewers who join
 * mid-broadcast get their bearings from this in about a second, which is the
 * single most valuable thing a permanent graphic can do on a rolling channel.
 *
 * It is drawn with one Graphics per column rather than per slot: five draw calls
 * instead of sixty-two.
 */
export class BracketLadder {
  readonly view = new Container();

  private readonly columns: Container[] = [];
  private readonly slotLabels = new Map<string, { home: Text; away: Text; frame: Graphics }>();
  private currentMatchId: string | null = null;

  constructor() {
    this.view.eventMode = 'none';
    this.view.visible = false;
    this.view.alpha = 0;
  }

  /**
   * Scale and place the ladder so it fits a box, and report whether the result
   * is still legible.
   *
   * Every column of a bracket spans the same total height by construction, so
   * the ladder cannot shrink as the tournament narrows - it is ~1160px tall
   * whether it is showing sixteen ties or one. That is why this returns a
   * verdict instead of just scaling: below `minScale` the nation codes turn to
   * mush, and a smear of unreadable text is worse than no panel at all.
   * (It also fixes the ladder running off the bottom of the frame, which it did
   * at the fixed 0.92 it used to be pinned to.)
   */
  fitInto(x: number, y: number, width: number, height: number, minScale = 0.55): boolean {
    const bounds = this.view.getLocalBounds();
    if (bounds.width <= 0 || bounds.height <= 0) return false;

    const scale = Math.min(width / bounds.width, height / bounds.height, 1);
    this.view.scale.set(scale);
    this.view.position.set(
      x + (width - bounds.width * scale) / 2,
      y + (height - bounds.height * scale) / 2,
    );

    return scale >= minScale;
  }

  setVisible(visible: boolean, duration = 0.6): void {
    if (visible) this.view.visible = true;
    gsap.to(this.view, {
      alpha: visible ? 1 : 0,
      duration,
      ease: 'sine.inOut',
      onComplete: () => {
        if (!visible) this.view.visible = false;
      },
    });
  }

  /** Rebuild from a bracket snapshot. Cheap enough to call on every snapshot. */
  render(bracket: BracketState | null): void {
    this.clear();
    if (!bracket) return;

    bracket.rounds.forEach((round, columnIndex) => {
      const column = new Container();
      column.x = columnIndex * COLUMN_WIDTH;

      const heading = new Text({
        text: round.label,
        style: {
          fontFamily: 'Bahnschrift, "Segoe UI", system-ui, sans-serif',
          fontSize: 13,
          fontWeight: '700',
          fill: 0x64768f,
          letterSpacing: 2,
        },
      });
      heading.anchor.set(0.5, 0);
      heading.x = COLUMN_WIDTH / 2 - 8;
      column.addChild(heading);

      const pairHeight = SLOT_HEIGHT * 2 + SLOT_GAP;
      const spacing = Math.pow(2, columnIndex) * (pairHeight + 14);

      round.slots.forEach((slot, slotIndex) => {
        const top = 26 + slotIndex * spacing + (spacing - pairHeight) / 2;

        const frame = new Graphics()
          .roundRect(0, top, COLUMN_WIDTH - 16, pairHeight, 6)
          .fill({ color: 0x0a1220, alpha: 0.55 })
          .stroke({ width: 1, color: 0x2a3b57, alpha: 0.8 });

        const home = this.slotText(slot.home, slot.winner === slot.home && slot.winner !== null);
        home.position.set(10, top + 5);

        const away = this.slotText(slot.away, slot.winner === slot.away && slot.winner !== null);
        away.position.set(10, top + 5 + SLOT_HEIGHT);

        const scores = new Text({
          text: slot.winner ? `${slot.homeScore}-${slot.awayScore}` : '',
          style: {
            fontFamily: 'Consolas, monospace',
            fontSize: 12,
            fill: 0x64768f,
          },
        });
        scores.anchor.set(1, 0);
        scores.position.set(COLUMN_WIDTH - 24, top + pairHeight - 17);

        column.addChild(frame, home, away, scores);
        this.slotLabels.set(slot.matchId, { home, away, frame });
      });

      this.view.addChild(column);
      this.columns.push(column);
    });

    if (this.currentMatchId) this.highlight(this.currentMatchId);
  }

  /** Ring the tie that is being played right now. */
  highlight(matchId: string | null): void {
    if (this.currentMatchId && this.currentMatchId !== matchId) {
      const previous = this.slotLabels.get(this.currentMatchId);
      if (previous) gsap.killTweensOf(previous.frame);
    }

    this.currentMatchId = matchId;
    if (!matchId) return;

    const entry = this.slotLabels.get(matchId);
    if (!entry) return;

    gsap.fromTo(
      entry.frame,
      { alpha: 1 },
      { alpha: 0.45, duration: 0.7, yoyo: true, repeat: -1, ease: 'sine.inOut' },
    );
    entry.home.style.fill = 0xffffff;
    entry.away.style.fill = 0xffffff;
  }

  private slotText(code: string | null, isWinner: boolean): Text {
    return new Text({
      text: code ?? '—',
      style: {
        fontFamily: 'Bahnschrift, "Segoe UI", system-ui, sans-serif',
        fontSize: 15,
        fontWeight: isWinner ? '700' : '400',
        fill: isWinner ? 0xffc94d : code ? 0x9bb0cc : 0x3d4c63,
        letterSpacing: 1.5,
      },
    });
  }

  private clear(): void {
    for (const column of this.columns) column.destroy({ children: true });
    this.columns.length = 0;
    this.slotLabels.clear();
  }
}
